import { useQuery } from "@tanstack/react-query"
import { Plus } from "lucide-react"
import { useRef, useState } from "react"
import { useNavigate } from "react-router"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import { KpiDocumentosTH } from "@/componentes/kpi/KpiTalento"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"
import { GenerarDocumento } from "./GenerarDocumento"
import { type DocumentoTHResumen, ESTADOS, type EstadoDocumento } from "./tipos"

const fecha = (iso: string) => new Date(iso).toLocaleDateString("es-VE")

/**
 * Los documentos de Talento Humano en lista: todos (`th`) o los propios
 * (`autoservicio`), con el botón para generar uno nuevo.
 *
 * ⚠ **Lo pendiente va primero en el filtro por omisión de quien resuelve**: es lo
 * único de la lista que espera a alguien. Quien sólo emite arranca en «Todos».
 */
export function ListaDocumentosTH({ modo }: { modo: "th" | "autoservicio" }) {
  const navegar = useNavigate()
  const usuario = usarSesion((e) => e.usuario)
  const enTH = modo === "th"
  const puedeGenerar = enTH ? tienePermiso(usuario, "documento_th:emitir") : true
  const [estado, setEstado] = useState<EstadoDocumento | "">(
    enTH && tienePermiso(usuario, "documento_th:resolver") ? "SOLICITADO" : "",
  )
  const [generando, setGenerando] = useState(false)

  const ruta = enTH ? "documentos-th" : "mis-documentos"
  const lista = useQuery({
    queryKey: [enTH ? "documentos-th" : "mis-documentos", "lista", estado],
    queryFn: () =>
      api.get<DocumentoTHResumen[]>(`/api/v1/${ruta}${estado ? `?estado=${estado}` : ""}`),
  })
  const vista = useRef<HTMLDivElement | null>(null)
  const titulo = enTH ? "Documentos de Talento Humano" : "Mis documentos"
  const abrir = (id: number) =>
    navegar(enTH ? `/talento-humano/documento/${id}` : `/mis-documentos/${id}`)

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div>
          <label htmlFor={`th-estado-${modo}`} className="mb-1 block text-xs font-medium uppercase">
            Estado
          </label>
          <select
            id={`th-estado-${modo}`}
            className="campo"
            value={estado}
            onChange={(e) => setEstado(e.target.value as EstadoDocumento | "")}
          >
            <option value="">Todos</option>
            {Object.entries(ESTADOS).map(([valor, e]) => (
              <option key={valor} value={valor}>
                {e.etiqueta}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto flex flex-wrap items-start gap-2">
          {!!lista.data?.length && (
            <Exportar
              titulo={titulo}
              subtitulo={`Estado: ${estado ? ESTADOS[estado].etiqueta : "Todos"}`}
              contenido={vista}
            />
          )}
          {puedeGenerar && (
            <Boton variante="primario" onClick={() => setGenerando(true)}>
              <Plus size={15} aria-hidden="true" />
              {enTH ? "Generar documento" : "Nueva solicitud"}
            </Boton>
          )}
        </div>
      </div>
      {enTH && <KpiDocumentosTH />}

      {lista.isError ? (
        <EstadoError
          titulo="No se pudo leer la lista."
          error={lista.error}
          onReintentar={() => lista.refetch()}
        />
      ) : (
        <div ref={vista}>
          <Tabla etiqueta={titulo}>
            <Cabecera>
              <Th>Código</Th>
              <Th>Documento</Th>
              {enTH && <Th>Empleado</Th>}
              <Th>Fecha</Th>
              <Th>Estado</Th>
            </Cabecera>
            <Cuerpo>
              {lista.isPending ? (
                <TablaVacia>Cargando…</TablaVacia>
              ) : (lista.data ?? []).length === 0 ? (
                <TablaVacia>
                  {estado
                    ? "Nada en ese estado. Prueba con «Todos»."
                    : enTH
                      ? "Todavía no se ha generado ningún documento."
                      : "Todavía no has pedido nada."}
                </TablaVacia>
              ) : (
                (lista.data ?? []).map((d) => (
                  <Fila
                    key={d.id}
                    className="cursor-pointer"
                    atenuada={d.estado === "ANULADO"}
                    onClick={() => abrir(d.id)}
                  >
                    <Td numero={true} data-exportar-valor={d.codigo}>
                      <button
                        type="button"
                        className="foco-cristal font-medium underline-offset-2 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation()
                          abrir(d.id)
                        }}
                      >
                        {d.codigo}
                      </button>
                    </Td>
                    <Td>{d.titulo}</Td>
                    {enTH && <Td>{d.persona ?? "—"}</Td>}
                    <Td numero={true}>{fecha(d.creado_en)}</Td>
                    <Td>
                      <Insignia tono={ESTADOS[d.estado].tono}>
                        {ESTADOS[d.estado].etiqueta}
                      </Insignia>
                    </Td>
                  </Fila>
                ))
              )}
            </Cuerpo>
          </Tabla>
        </div>
      )}

      {generando && (
        <GenerarDocumento
          modo={modo}
          abierto={generando}
          onCerrar={() => setGenerando(false)}
          onGenerado={(doc) => {
            setGenerando(false)
            abrir(doc.id)
          }}
        />
      )}
    </div>
  )
}
